import type { LogEventAction } from '../constants';
import { refsEqual } from '../refs';
import type { Actor } from '../seats';
import type { FlowPhaseRow } from '../stage-gates';
import type { Command, WorkItemState } from '../types';
import type {
  AccessContext,
  Answer,
  ChildContext,
  Context,
  MembershipContext,
  Mode,
  Origin,
  OriginItem,
  OriginKind,
  PlanStep,
  Policy,
  QuestionClass,
  TickMeans,
  Weight,
} from './types';

/**
 * **Step 2: the context** (docs/RESOLUTION.md §1, §9). Gathered by the caller,
 * never fetched here: the read layer gathers off the pooled `db` (viewer-scoped)
 * and the write spine gathers in the transaction (FOR UPDATE), and both feed
 * `assembleContext`, which is §9's one condition for keeping the dry run from
 * becoming a second path.
 *
 * The log-side recovery lives here too (`commandOfEvent`, `RETIRED_ACTIONS`,
 * §8's origin marker): the steps it reads back out of the log ARE a piece of
 * context (`Context.originSteps`), assembled before the pipeline runs rather
 * than by any step of it.
 */

/**
 * One stage as the resolver reads it: its key, its word, and what moving there
 * APPLIES. The GATE is the caller's to evaluate (`MembershipContext.gate`) -
 * both server substrates read one verdict off the stage steps rather than
 * gating twice - so a caller that holds the derived stage steps and not the authored
 * definition (the item page) can still speak this shape.
 */
export type StageFacts = Pick<FlowPhaseRow, 'key' | 'label' | 'means'> & {
  /** The stage's gate, as far as the review reads it: a review stage's
   *  gatekeeper is a reviewer beside the requester seat (2026-11-03). */
  gate?: Pick<FlowPhaseRow['gate'], 'gatekeeper'>;
};

/** One flow the item belongs to, as both substrates gather it. */
export interface FlowFacts {
  flowId: string;
  flowName?: string;
  /** The member's logged position. */
  stageKey: string;
  /** The flow's stages, as authored. */
  rows: readonly StageFacts[];
}

/**
 * The raw facts a context is assembled from. The read layer gathers them off
 * the pooled `db` (gated, viewer-scoped); the write spine gathers them in the
 * transaction (FOR UPDATE). Both feed THIS function, so the dry run and the
 * act cannot drift apart - §9's one condition for keeping the dry run from
 * becoming a second path.
 */
export interface ContextFacts {
  item: WorkItemState;
  actor: Actor;
  access: AccessContext;
  weight: Weight;
  mode: Mode;
  flows: readonly FlowFacts[];
  /** The stage a `move` stimulus targets, with its gate verdict. */
  target?: { flowId: string; stageKey: string; gate: MembershipContext['gate'] };
  answers?: readonly Answer[];
  policies?: Partial<Record<QuestionClass, Policy>>;
  grammar?: { tickMeans?: TickMeans };
  children?: readonly ChildContext[];
  /** Open descendants the viewer may not see: gathered with `children`, never
   *  apart from it. */
  hiddenChildren?: number;
  /** The review duty, when the caller holds the ANSWER rather than the flows
   *  behind it: a list row carries it (`ListRow.reviewRequired`), and a client
   *  running the resolver optimistically has the row. Absent = read it off the
   *  flows, as both server substrates do. */
  reviewRequired?: boolean;
  /** A seat on the item is offered to someone (the edge table's `onlyParty`
   *  reads it). Absent = none, as a list row's resolver has it. */
  seatOffered?: boolean;
  origin?: Origin;
  originSteps?: readonly PlanStep[];
  /** The other items that plan touched - a tick's sweep (§6) - as the un-tick
   *  needs them to fold its inverse. */
  originItems?: readonly OriginItem[];
}

/**
 * An **authored review duty** (ruled by Leon 2026-09-21): a flow this item
 * joined says one of its stages MEANS `verdict: accepted`, so the process the
 * item consented to demands a judgment. *"Skipping gates that were willfully
 * applied should hold. If you don't want that, remove the gate first and then
 * close."*
 *
 * The verdict no longer waives it (2026-11-03): the verdict is the record of
 * the LAST review, and an item a flow moved back to open with an Accepted
 * record still owes the next one. The duty matters only on an open item (the
 * edge table's Ready for review instead of Done), so a done item's record
 * changes nothing here.
 *
 * The item's own Review required setting is the other source; `reviewRequired`
 * in the context is the OR of both.
 */
export function authoredReviewDuty(
  _item: Pick<WorkItemState, 'verdict'>,
  flows: readonly ReviewDutySource[],
): boolean {
  return flows.some((f) =>
    'authorsReviewDuty' in f ? f.authorsReviewDuty : stagesAuthorReviewDuty(f.rows),
  );
}

/**
 * The REVIEWERS a flow names beside the requester seat (docs/CLOSED_AXIS.md
 * "Finishing", 2026-11-03): the gatekeeper of every stage that means
 * `verdict: accepted`. A ref may be a group's: `holdsSeat` reads it.
 */
export function flowReviewers(
  flows: readonly { readonly rows: readonly StageFacts[] }[],
): string[] {
  const out: string[] = [];
  for (const flow of flows) {
    for (const row of flow.rows) {
      const keeper = row.gate?.gatekeeper;
      if (!keeper || out.includes(keeper)) continue;
      if (row.means.some((p) => p.attr === 'verdict' && p.value === 'accepted')) out.push(keeper);
    }
  }
  return out;
}

/** Whether a flow's stage `means` apply `delivered`: the stage move hands the
 *  work in, so the edge table offers no Ready for review row of its own. */
export function flowsCoverDelivery(
  flows: readonly { readonly rows: readonly Pick<StageFacts, 'means'>[] }[],
): boolean {
  return flows.some((f) => f.rows.some((r) => r.means.some((p) => p.attr === 'delivered')));
}

/**
 * The flow half of {@link authoredReviewDuty}: one of these stages MEANS
 * `verdict: accepted`. A read that hands out memberships rather than stages
 * answers it once per flow (`ItemFlowMembership.authorsReviewDuty`,
 * src/read/flows.ts), so every surface reads the one definition.
 */
export function stagesAuthorReviewDuty(rows: readonly Pick<StageFacts, 'means'>[]): boolean {
  return rows.some((row) =>
    row.means.some((pair) => pair.attr === 'verdict' && pair.value === 'accepted'),
  );
}

/** A flow as the review duty reads it: its stages (the resolver's
 *  `FlowFacts`), or a read's answer about them (a membership's
 *  `authorsReviewDuty`). */
export type ReviewDutySource =
  | { readonly rows: readonly Pick<StageFacts, 'means'>[] }
  | { readonly authorsReviewDuty: boolean };

/** Assemble the context both substrates share. Pure. */
export function assembleContext(facts: ContextFacts): Context {
  const memberships: MembershipContext[] = facts.flows.map((flow) => {
    const current = flow.rows.find((r) => r.key === flow.stageKey);
    const targeted =
      facts.target?.flowId === flow.flowId
        ? flow.rows.find((r) => r.key === facts.target?.stageKey)
        : undefined;
    return {
      flowId: flow.flowId,
      ...(flow.flowName ? { flowName: flow.flowName } : {}),
      stageKey: flow.stageKey,
      stageLabel: current?.label ?? flow.stageKey,
      ...(targeted
        ? { target: { key: targeted.key, label: targeted.label, means: targeted.means } }
        : {}),
      gate: facts.target?.flowId === flow.flowId ? facts.target.gate : { allows: true },
    };
  });

  return {
    item: facts.item,
    actor: facts.actor,
    access: facts.access,
    memberships,
    weight: facts.weight,
    ...(facts.children ? { children: facts.children } : {}),
    ...(facts.hiddenChildren ? { hiddenChildren: facts.hiddenChildren } : {}),
    mode: facts.mode,
    ...(facts.policies ? { policies: facts.policies } : {}),
    answers: facts.answers ?? [],
    ...(facts.grammar ? { grammar: facts.grammar } : {}),
    // The review duty from either source (2026-11-03): the item's own Review
    // required setting, or a flow stage that means `verdict: accepted`.
    reviewRequired:
      facts.reviewRequired ??
      ((facts.item.reviewRequired ?? false) || authoredReviewDuty(facts.item, facts.flows)),
    reviewers: flowReviewers(facts.flows),
    seatOffered: facts.seatOffered ?? false,
    deliveryCovered: flowsCoverDelivery(facts.flows),
    ...(facts.origin ? { origin: facts.origin } : {}),
    ...(facts.originSteps ? { originSteps: facts.originSteps } : {}),
    ...(facts.originItems?.length ? { originItems: facts.originItems } : {}),
  };
}

/**
 * **The actions a REPLAY ignores.** Both belonged to the deferred close, built
 * 2026-09-21 and retired 2026-09-22 with its table (docs/RESOLUTION.md §6):
 * the enum words stay (a log is append-only and the migration rule is add-only
 * for enum values), and the rows they projected are gone. Anything that reads
 * the log back therefore skips them EXPLICITLY rather than meeting them at a
 * `default` - a plan that appended one is still recoverable, which is what an
 * un-tick over a tick taken before the retirement depends on.
 *
 * The one reader of the origin marker uses this: `recoverOriginPlan`
 * (`src/read/resolution-context.ts`), which the dry run calls over the pooled
 * `db` and the write spine inside its transaction.
 */
export const RETIRED_ACTIONS: readonly LogEventAction[] = [
  'close_deferred',
  'close_deferral_retracted',
];

/** The plan kind a plain un-tick takes back (`untickOrigin`). A plan without
 *  a kind is read as one. */
export const UNTICK_TAKES_BACK = 'tick' satisfies OriginKind;

/**
 * The plan kinds that decide an un-tick (`untickOrigin`): a TICK plan is the
 * one taken back, and a newer UN-TICK plan may say it already was. A plan of
 * any other kind is skipped over; a plan without a kind decides like a tick.
 *
 * The read layer filters on this list in SQL (`recoverOriginPlan`,
 * `src/read/resolution-context.ts`) and reads from the item's newest tick
 * upward, so it returns that tick and the un-ticks above it, however many
 * skipped plans sit among them. One list, so the query and the rule cannot
 * drift.
 */
export const UNTICK_DECIDING_KINDS = [
  UNTICK_TAKES_BACK,
  'untick',
] as const satisfies readonly OriginKind[];

/**
 * The plan an un-tick takes back (§2, §8): the newest TICK plan on the item,
 * or, when the un-tick names one (`originKey`, a surface's undo of its own
 * statement), exactly that plan. `origins` are the item's origin-marked plans,
 * newest first, one per plan.
 *
 * A later plan of another kind (a chip's Tag or Untag, a retag, a retitle, an
 * act) is skipped over, never inverted by an un-tick: "a tick says I am done",
 * and the un-tick takes back exactly that statement. The kinds that are not
 * skipped are `UNTICK_DECIDING_KINDS`.
 *
 * A newer UN-TICK plan that took back this tick (its `undone` names the tick's
 * key) means there is nothing to take back (`null`, and the un-tick refuses)
 * rather than a second inversion of the same tick. A newer un-tick that took
 * back another plan (a named undo of a tag, the retag dialog's Undo) is
 * skipped over (2026-10-18). An un-tick plan written before 2026-10-18 has no
 * `undone`, and keeps the old reading: the newest tick is taken back.
 *
 * A plan without a kind was written before 2026-09-29, when every plan was a
 * candidate; it stays one, so a tick made before the kind was recorded can
 * still be taken back.
 */
export function untickOrigin(origins: readonly Origin[], originKey?: string): Origin | null {
  if (originKey !== undefined) {
    return origins.find((o) => o.stimulusKey === originKey) ?? null;
  }
  const deciding: readonly OriginKind[] = UNTICK_DECIDING_KINDS;
  const undone = new Set<string>();
  for (const origin of origins) {
    const kind = origin.stimulusKind;
    if (kind !== undefined && !deciding.includes(kind)) continue;
    if (kind === 'untick') {
      if (origin.undone === undefined) return null;
      undone.add(origin.undone);
      continue;
    }
    return undone.has(origin.stimulusKey) ? null : origin;
  }
  return null;
}

/** One event of a plan, as the log holds it. */
export interface PlanEvent {
  action: LogEventAction;
  principal: string;
  previousValue: Record<string, unknown> | null;
  newValue: Record<string, unknown> | null;
}

/**
 * The command an event stood for - how an un-tick recovers the steps of the
 * plan its origin names (§2: the inverse is found through the origin, never a
 * hand-written table). `null` for an event whose command cannot be read back
 * from the action alone; a plan carrying one of those is not recoverable, and
 * the un-tick refuses rather than inverting half of it.
 *
 * `reassigned` is the one act that needs the delta: a Claim seats exactly the
 * principal into the contributor set, and that is what reads back as `Claim`;
 * any other seat move reads back as the `Reassign` it was, which has no
 * inverse from the step alone (`inverseOf`).
 */
export function commandOfEvent(event: PlanEvent): Command | null {
  switch (event.action) {
    case 'tendered':
      return { type: 'Tender' };
    case 'tender_retracted':
      return { type: 'Retract' };
    case 'concluded':
      return { type: 'Close' };
    case 'reopened':
      return { type: 'Reopen' };
    case 'accepted':
      return { type: 'Accept' };
    case 'disputed':
      return { type: 'Dispute' };
    case 'resolved':
      return { type: 'Resolve' };
    case 'activity_declared': {
      const active = event.newValue?.['active'];
      return typeof active === 'boolean' ? { type: 'SetActive', active } : null;
    }
    case 'reassigned': {
      const before = event.previousValue?.['contributors'];
      const after = event.newValue?.['contributors'];
      if (!Array.isArray(before) || !Array.isArray(after)) return null;
      const added = after.filter((r) => !before.some((p) => refsEqual(String(p), String(r))));
      if (added.length === 1 && refsEqual(String(added[0]), event.principal)) {
        return { type: 'Claim' };
      }
      return { type: 'Reassign', role: 'contributor', to: after.map(String) };
    }
    // The user tag acts (docs/FIELDS_AND_TAGS.md §6.2): the event carries the
    // spelling, so a retag's plan reads back as the Untag/Tag pairs it was.
    case 'item_tagged': {
      const tag = event.newValue?.['tag'];
      return typeof tag === 'string' ? { type: 'Tag', tag } : null;
    }
    case 'item_untagged': {
      const tag = event.previousValue?.['tag'];
      return typeof tag === 'string' ? { type: 'Untag', tag } : null;
    }
    default:
      return null;
  }
}
